"use client";

import { useState, useRef, useCallback } from "react";
import { search, addToQueue, type SearchTrack } from "@/lib/api";

interface Props {
  onQueued?: (track: SearchTrack) => void;
  onQueueFailed?: (uri: string) => void;
}

type Feedback =
  | { id: number; kind: "queued"; title: string }
  | { id: number; kind: "duplicate"; title: string; retryIn: number }
  | { id: number; kind: "blocked"; title: string }
  | { id: number; kind: "error"; title: string }
  | null;

export default function SearchBar({ onQueued, onQueueFailed }: Props) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchTrack[]>([]);
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState<Feedback>(null);
  const feedbackTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const feedbackSeq = useRef(0);

  const doSearch = useCallback(async (q: string) => {
    if (!q.trim()) {
      setResults([]);
      return;
    }
    setLoading(true);
    try {
      const data = await search(q);
      setResults(data.tracks || []);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  function flashFeedback(next: Omit<NonNullable<Feedback>, "id">, ms = 2400) {
    feedbackSeq.current += 1;
    setFeedback({ ...next, id: feedbackSeq.current });
    if (feedbackTimer.current) clearTimeout(feedbackTimer.current);
    feedbackTimer.current = setTimeout(() => setFeedback(null), ms);
  }

  function handleSearchSubmit(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") doSearch(query);
  }

  async function handleTap(track: SearchTrack) {
    // Show immediately — don't wait for the API.
    setQuery("");
    setResults([]);
    flashFeedback({ kind: "queued", title: track.title });
    onQueued?.(track);

    try {
      const res = await addToQueue({
        uri: track.uri,
        title: track.title,
        artist: track.artist,
        album_art: track.album_art,
      });
      if (res.status === "duplicate") {
        onQueueFailed?.(track.uri);
        flashFeedback({
          kind: "duplicate",
          title: track.title,
          retryIn: res.retry_after_seconds ?? 0,
        });
      } else if (res.status === "blocked") {
        onQueueFailed?.(track.uri);
        flashFeedback({ kind: "blocked", title: track.title });
      }
    } catch {
      onQueueFailed?.(track.uri);
      flashFeedback({ kind: "error", title: track.title });
    }
  }

  function formatDuration(ms: number) {
    const s = Math.floor(ms / 1000);
    return `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, "0")}`;
  }

  return (
    <div className="w-full space-y-2">
      <input
        type="text"
        placeholder="Search for a song..."
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={handleSearchSubmit}
        className="w-full px-4 py-3 rounded-xl bg-[var(--surface)] border border-[var(--border)] text-[var(--foreground)] placeholder:text-[var(--muted)] outline-none focus:border-[var(--accent)] min-h-[44px]"
      />
      {loading && <p className="text-sm text-[var(--muted)]">Searching...</p>}

      {feedback && (
        <div
          key={feedback.id}
          className="flash-in w-full rounded-xl px-4 py-4 text-center"
          style={{
            background:
              feedback.kind === "queued"
                ? "var(--accent)"
                : feedback.kind === "duplicate"
                ? "var(--surface)"
                : "#3a1414",
            color:
              feedback.kind === "queued"
                ? "var(--background)"
                : feedback.kind === "duplicate"
                ? "var(--muted)"
                : "#ffb4b4",
            border:
              feedback.kind === "queued"
                ? "none"
                : "1px solid var(--border)",
            boxShadow:
              feedback.kind === "queued"
                ? "0 0 24px rgba(192,38,211,0.4)"
                : "none",
          }}
        >
          {feedback.kind === "queued" && (
            <>
              <div className="text-2xl font-extrabold tracking-wider">ADDED ✓</div>
              <div className="text-sm font-medium opacity-90 mt-1 truncate">
                {feedback.title}
              </div>
            </>
          )}
          {feedback.kind === "duplicate" && (
            <>
              <div className="text-lg font-bold">Already in queue</div>
              <div className="text-sm mt-1 truncate">
                {feedback.title}
                {feedback.retryIn > 0 ? ` — try again in ${feedback.retryIn}s` : ""}
              </div>
            </>
          )}
          {feedback.kind === "blocked" && (
            <>
              <div className="text-lg font-bold">Not allowed</div>
              <div className="text-sm mt-1 truncate">
                {feedback.title} — pick a different song
              </div>
            </>
          )}
          {feedback.kind === "error" && (
            <>
              <div className="text-lg font-bold">Couldn&apos;t queue</div>
              <div className="text-sm mt-1 truncate">{feedback.title} — try again</div>
            </>
          )}
        </div>
      )}

      {results.length > 0 && (
        <div className="space-y-1">
          {results.map((t) => (
            <button
              key={t.uri}
              onClick={() => handleTap(t)}
              className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-[var(--surface)] transition-colors text-left min-h-[44px]"
            >
              {t.album_art ? (
                <img src={t.album_art} alt="" className="w-10 h-10 rounded" />
              ) : (
                <div className="w-10 h-10 rounded bg-[var(--border)]" />
              )}
              <div className="flex-1 min-w-0">
                <p className="truncate font-medium">{t.title}</p>
                <p className="truncate text-sm text-[var(--muted)]">
                  {t.artist} · {formatDuration(t.duration_ms)}
                </p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
